import { ActionIcon, Button, Group, Indicator, Paper, Popover, SegmentedControl, Text, Tooltip } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useHotkeys } from '@mantine/hooks';
import { IconClock, IconCoffee, IconCurrencyDollar, IconForms, IconList, IconPlayerPlayFilled, IconPlayerStopFilled, IconX } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  toRequest,
  useCreateEntry,
  useDeleteEntry,
  useRunningTimer,
  useStopTimer,
  useUpdateEntry,
  type TimeEntry,
} from '@/api/timeEntries';
import { parseValues, useEntryFields, type CustomFieldValues } from '@/api/customFields';
import { useCurrentWorkspace } from '@/app/WorkspaceContext';
import { CustomFieldInputs } from '@/components/CustomFieldInputs';
import { missingRequired, withDefaults } from '@/components/customFieldHelpers';
import { DurationInput } from '@/components/DurationInput';
import { ProjectPicker, type ProjectSelection } from '@/components/ProjectPicker';
import { TagPicker } from '@/components/TagPicker';
import { TimeInput } from '@/components/TimeInput';
import { useApiError } from '@/hooks/useApiError';
import { useNow } from '@/hooks/useNow';
import dayjs from '@/lib/dayjs';
import { formatDuration, timeFormat } from '@/lib/format';
import { DescriptionInput } from './DescriptionInput';
import { requiredFields } from './requiredFields';

interface Draft {
  description: string;
  selection: ProjectSelection;
  tagIds: string[];
  billable: boolean | null;
  isBreak: boolean;
  customFields: CustomFieldValues;
}

const emptyDraft: Draft = { description: '', selection: {}, tagIds: [], billable: null, isBreak: false, customFields: {} };

/** Eingabeleiste für Timer und manuelle Einträge (TT-01/02). */
export function TimerBar({ userId }: { userId?: string }) {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const { workspaceId, prefs, workspace } = ctx;
  const required = requiredFields(ctx);
  const onError = useApiError();
  const running = useRunningTimer(workspaceId, userId);
  const create = useCreateEntry(workspaceId);
  const stop = useStopTimer(workspaceId);
  const update = useUpdateEntry(workspaceId);
  const discard = useDeleteEntry(workspaceId);
  const [mode, setMode] = useState<'timer' | 'manual'>(() => (localStorage.getItem('ft.trackerMode') as 'timer') ?? 'timer');
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const entry = running.data ?? null;
  const now = useNow(1000, !!entry);

  // Manueller Modus: Datum, Start, Ende
  const today = dayjs().tz(prefs.timezone).startOf('day');
  const [manualDate, setManualDate] = useState(today.format('YYYY-MM-DD'));
  const [manualStart, setManualStart] = useState<[number, number]>([9, 0]);
  const [manualEnd, setManualEnd] = useState<[number, number]>([10, 0]);

  useEffect(() => {
    localStorage.setItem('ft.trackerMode', mode);
  }, [mode]);

  // Laufenden Timer in die Leiste übernehmen
  useEffect(() => {
    if (entry) {
      setDraft({
        description: entry.description ?? '',
        selection: { projectId: entry.projectId, taskId: entry.taskId },
        tagIds: entry.tagIds,
        billable: entry.billable,
        isBreak: entry.type === 'BREAK',
        customFields: parseValues(entry.customFields),
      });
    }
  }, [entry?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fields = useEntryFields(workspaceId, draft.selection.projectId, ctx.hasFeature('CUSTOM_FIELDS'));
  const cfJson = (values: CustomFieldValues) => (fields.length > 0 ? JSON.stringify(withDefaults(fields, values)) : undefined);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  // Werte des laufenden Timers erst beim Schließen speichern
  const toggleFields = (open: boolean) => {
    setFieldsOpen(open);
    if (!open && entry) patchRunning({ customFields: draft.customFields });
  };
  const cfMissing = missingRequired(fields, withDefaults(fields, draft.customFields)).length;

  const defaultBillable = workspace.settings.tracking.defaultBillable;
  const billable = draft.billable ?? defaultBillable;

  /** Änderungen am laufenden Timer sofort speichern. */
  const patchRunning = (changes: Partial<Draft>) => {
    const next = { ...draft, ...changes };
    setDraft(next);
    if (entry) {
      update.mutate(
        {
          id: entry.id,
          ...toRequest(entry, {
            description: next.description,
            projectId: next.selection.projectId ?? undefined,
            taskId: next.selection.taskId ?? undefined,
            tagIds: next.tagIds,
            billable: next.billable ?? undefined,
            customFields: changes.customFields ? cfJson(next.customFields) : undefined,
          }),
        },
        { onError },
      );
    }
  };

  const start = () => {
    create.mutate(
      {
        userId,
        description: draft.description,
        projectId: draft.selection.projectId ?? undefined,
        taskId: draft.selection.taskId ?? undefined,
        tagIds: draft.tagIds,
        billable: draft.billable ?? undefined,
        type: draft.isBreak ? 'BREAK' : 'REGULAR',
        customFields: draft.isBreak ? undefined : cfJson(draft.customFields),
      },
      { onError },
    );
  };

  const stopTimer = () => {
    stop.mutate(
      { userId },
      {
        onSuccess: () => setDraft(emptyDraft),
        onError,
      },
    );
  };

  const manualRange = () => {
    const base = dayjs.tz(manualDate, prefs.timezone);
    const s = base.hour(manualStart[0]).minute(manualStart[1]);
    let e = base.hour(manualEnd[0]).minute(manualEnd[1]);
    if (!e.isAfter(s)) e = e.add(1, 'day');
    return [s, e] as const;
  };

  const addManual = () => {
    const [s, e] = manualRange();
    create.mutate(
      {
        userId,
        description: draft.description,
        projectId: draft.selection.projectId ?? undefined,
        taskId: draft.selection.taskId ?? undefined,
        tagIds: draft.tagIds,
        billable: draft.billable ?? undefined,
        start: s.toISOString(),
        end: e.toISOString(),
        type: draft.isBreak ? 'BREAK' : 'REGULAR',
        customFields: draft.isBreak ? undefined : cfJson(draft.customFields),
      },
      { onSuccess: () => setDraft(emptyDraft), onError },
    );
  };

  const breaksEnabled = workspace.settings.tracking.breaksEnabled;
  const isBreak = entry ? entry.type === 'BREAK' : draft.isBreak;

  // Tastenkürzel (UI-08): s = Start/Stopp, b = Pausenmodus, / = Beschreibung fokussieren
  useHotkeys([
    ['s', () => (entry ? stopTimer() : mode === 'timer' && start())],
    ['b', () => !entry && breaksEnabled && setDraft((d) => ({ ...d, isBreak: !d.isBreak }))],
    ['/', () => document.getElementById('tracker-description')?.focus()],
  ]);

  const elapsed = entry ? (now - dayjs(entry.start).valueOf()) / 1000 : 0;
  const [ms, me] = manualRange();
  const tf = timeFormat(prefs);

  return (
    <Paper withBorder p="xs" radius="md" shadow="xs">
      <Group gap="xs" wrap="wrap">
        <DescriptionInput
          value={draft.description}
          onChange={(description) => setDraft((d) => ({ ...d, description }))}
          onBlur={() => entry && entry.description !== draft.description && patchRunning({})}
          onSelectSuggestion={(s) =>
            (entry ? patchRunning : (c: Partial<Draft>) => setDraft((d) => ({ ...d, ...c })))({
              description: s.description,
              selection: { projectId: s.projectId, taskId: s.taskId },
              tagIds: s.tagIds,
              billable: s.billable,
            })
          }
          onEnter={() => (entry ? patchRunning({}) : mode === 'timer' ? start() : addManual())}
          id="tracker-description"
          placeholder={isBreak ? t('tracker.breakPlaceholder') : t('tracker.whatAreYouWorkingOn')}
          variant="unstyled"
          style={{ flex: 1, minWidth: 220 }}
          px="xs"
          aria-label={t('tracker.description')}
          error={undefined}
          rightSection={required.description ? <Text c="red">*</Text> : undefined}
        />
        {!isBreak && (
        <>
        <ProjectPicker
          value={draft.selection}
          required={required.project}
          onChange={(selection, project) =>
            (entry ? patchRunning : (c: Partial<Draft>) => setDraft((d) => ({ ...d, ...c })))({
              selection,
              billable: project ? project.billable : draft.billable,
            })
          }
        />
        <TagPicker
          value={draft.tagIds}
          required={required.tag}
          onChange={(tagIds) => (entry ? patchRunning({ tagIds }) : setDraft((d) => ({ ...d, tagIds })))}
        />
        {fields.length > 0 && (
          <Popover opened={fieldsOpen} onChange={toggleFields} width={300} position="bottom-end" withArrow shadow="md">
            <Popover.Target>
              <Tooltip label={t('customFields.entryFields')}>
                <Indicator color="red" size={8} disabled={cfMissing === 0} offset={4}>
                  <ActionIcon variant="subtle" color="gray" onClick={() => toggleFields(!fieldsOpen)} aria-label={t('customFields.entryFields')}>
                    <IconForms size={18} />
                  </ActionIcon>
                </Indicator>
              </Tooltip>
            </Popover.Target>
            <Popover.Dropdown>
              <CustomFieldInputs
                fields={fields}
                values={withDefaults(fields, draft.customFields)}
                isAdmin={ctx.isAdmin}
                onChange={(customFields) => setDraft((d) => ({ ...d, customFields }))}
              />
            </Popover.Dropdown>
          </Popover>
        )}
        <Tooltip label={billable ? t('tracker.billable') : t('tracker.nonBillable')}>
          <ActionIcon
            variant={billable ? 'light' : 'subtle'}
            color={billable ? 'orange' : 'gray'}
            onClick={() => (entry ? patchRunning({ billable: !billable }) : setDraft((d) => ({ ...d, billable: !billable })))}
            aria-label={t('tracker.billable')}
            aria-pressed={billable}
          >
            <IconCurrencyDollar size={18} />
          </ActionIcon>
        </Tooltip>
        </>
        )}
        {breaksEnabled && (
          <Tooltip label={t('tracker.breakMode')}>
            <ActionIcon
              variant={isBreak ? 'filled' : 'subtle'}
              color={isBreak ? 'teal' : 'gray'}
              disabled={!!entry}
              onClick={() => setDraft((d) => ({ ...d, isBreak: !d.isBreak }))}
              aria-label={t('tracker.breakMode')}
              aria-pressed={isBreak}
            >
              <IconCoffee size={18} />
            </ActionIcon>
          </Tooltip>
        )}

        {mode === 'timer' || entry ? (
          <>
            <Text fw={600} ff="monospace" fz="lg" miw={90} ta="right">
              {formatDuration(elapsed, 'FULL')}
            </Text>
            {entry ? (
              <>
                <Button color="red" leftSection={<IconPlayerStopFilled size={16} />} onClick={stopTimer} loading={stop.isPending}>
                  {t('tracker.stop')}
                </Button>
                <Tooltip label={t('tracker.discard')}>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    onClick={() => discard.mutate(entry.id, { onSuccess: () => setDraft(emptyDraft), onError })}
                    aria-label={t('tracker.discard')}
                  >
                    <IconX size={16} />
                  </ActionIcon>
                </Tooltip>
              </>
            ) : (
              <Button leftSection={<IconPlayerPlayFilled size={16} />} onClick={start} loading={create.isPending}>
                {t('tracker.start')}
              </Button>
            )}
          </>
        ) : (
          <>
            <TimeInput
              display={ms.format(tf)}
              onCommit={(h, m) => setManualStart([h, m])}
              w={84}
              aria-label={t('tracker.startTime')}
            />
            <Text c="dimmed">–</Text>
            <TimeInput display={me.format(tf)} onCommit={(h, m) => setManualEnd([h, m])} w={84} aria-label={t('tracker.endTime')} />
            <DatePickerInput
              value={manualDate}
              onChange={(v) => v && setManualDate(dayjs(v).format('YYYY-MM-DD'))}
              valueFormat={prefs.dateFormat}
              w={130}
              aria-label={t('tracker.date')}
            />
            <DurationInput
              seconds={me.diff(ms, 'second')}
              onCommit={(seconds) => {
                const end = ms.add(seconds, 'second');
                setManualEnd([end.hour(), end.minute()]);
              }}
              w={90}
              aria-label={t('tracker.duration')}
            />
            <Button onClick={addManual} loading={create.isPending}>
              {t('tracker.add')}
            </Button>
          </>
        )}
        {!entry && (
          <SegmentedControl
            size="xs"
            value={mode}
            onChange={(v) => setMode(v as 'timer' | 'manual')}
            data={[
              { value: 'timer', label: <IconClock size={14} aria-label={t('tracker.timerMode')} /> },
              { value: 'manual', label: <IconList size={14} aria-label={t('tracker.manualMode')} /> },
            ]}
          />
        )}
      </Group>
    </Paper>
  );
}

export type { TimeEntry };
